import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getDefaultTicketDesign,
  getTicketDesignDetails,
} from "../services/ticketDesignApi";

export default function useTicketDesign(
  eventId,
  ticketId,
  type = "ticket_class",
  tag = "withoutProfilePic",
) {
  const cloneDesign = useCallback(
    (value) => (value ? JSON.parse(JSON.stringify(value)) : null),
    [],
  );
  const storageKey = `${eventId || "no-event"}:${ticketId || "no-ticket"}:${tag}`;
  const [design, setDesignState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialDesign, setInitialDesign] = useState(null);
  const [preGeneratePrintPdf, setPreGeneratePrintPdf] = useState(false);
  const [initialPreGeneratePrintPdf, setInitialPreGeneratePrintPdf] = useState(false);
  const [hasCertificate, setHasCertificate] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    (async () => {
      let stored = null;

      try {
        const [loadedDetails, defaultDesign] = await Promise.all([
          (eventId && ticketId)
            ? getTicketDesignDetails(eventId, ticketId, type, tag).catch(err => {
                console.error("useTicketDesign: loadDesign failed", err);
                return null;
              })
            : Promise.resolve(null),
          getDefaultTicketDesign(tag).catch(err => {
            console.error("useTicketDesign: getDefaultTicketDesign failed", err);
            return null;
          })
        ]);

        let loadedHasCert = loadedDetails?.hasCertificate !== undefined
          ? Boolean(loadedDetails?.hasCertificate)
          : (tag === "certificate" ? Boolean(loadedDetails?.design) : true);

        stored = loadedDetails?.design || (tag === "certificate" || tag === "forCertificatePrint" ? null : defaultDesign);

        if (tag === "badge" && stored?.canvas?.width === 320 && stored?.canvas?.height === 320) {
          if (defaultDesign && defaultDesign.canvas?.width === 242) {
            stored = defaultDesign;
            try {
              localStorage.removeItem(`qe_default_ticket_design_v5_badge`);
              localStorage.removeItem(`qe_ticket_design:${eventId}:${ticketId}:badge`);
            } catch (e) {}
          }
        }

        if (!stored && tag === "forCertificatePrint") {
          try {
            const raw = localStorage.getItem(`qe_ticket_design:${eventId}:${ticketId}:forCertificatePrint`);
            if (raw) {
              stored = JSON.parse(raw);
            }
          } catch {}

          if (!stored && (loadedHasCert || hasCertificate)) {
            try {
              const certDetails = await getTicketDesignDetails(eventId, ticketId, type, 'certificate');
              if (certDetails?.design) {
                stored = {
                  ...certDetails.design,
                  key: 'forCertificatePrint',
                  meta: { ...(certDetails.design?.meta || {}), template: 'forCertificatePrint' },
                  canvas: {
                    ...(certDetails.design?.canvas || {}),
                    printBackgroundImage: certDetails.design?.canvas?.printBackgroundImage !== false,
                  },
                };
                loadedHasCert = true;
              }
            } catch {}
          }
        }

        const effectiveHasCert = loadedHasCert || Boolean(stored);
        if (mounted) {
          setHasCertificate(effectiveHasCert);
        }

        if (mounted) {
          const loadedPreGeneratePrintPdf = Boolean(loadedDetails?.preGeneratePrintPdf);
          setPreGeneratePrintPdf(loadedPreGeneratePrintPdf);
          setInitialPreGeneratePrintPdf(loadedPreGeneratePrintPdf);
        }
      } catch (err) {
        console.error("useTicketDesign: failed to fetch designs", err);
      }

      if (!mounted) return;

      let finalDesign = stored ? { ...stored } : null;

      if (!finalDesign) {
        setInitialDesign(null);
        setDesignState(null);
        setPreGeneratePrintPdf(false);
        setInitialPreGeneratePrintPdf(false);
        setLoading(false);
        return;
      }

      const parsedCanvasBorderWidth = Number(finalDesign?.canvas?.borderWidth);
      finalDesign = {
        ...finalDesign,
        canvas: {
          ...(finalDesign.canvas || {}),
          borderWidth:
            Number.isFinite(parsedCanvasBorderWidth) &&
            parsedCanvasBorderWidth >= 0
              ? parsedCanvasBorderWidth
              : 1,
        },
      };

      if (tag === "withoutProfilePic" || tag === "forPrint") {
        if (finalDesign.sections?.profile) {
          finalDesign = {
            ...finalDesign,
            sections: {
              ...finalDesign.sections,
              profile: { ...finalDesign.sections.profile, enabled: false },
            },
            sectionsOrder: finalDesign.sectionsOrder.filter(
              (id) => id !== "profile",
            ),
          };
        }
      }

      // Migration: Convert ticketName from 'pill' to 'text'
      if (
        finalDesign?.sections?.ticketName &&
        finalDesign.sections.ticketName.type === "pill"
      ) {
        const oldPill = finalDesign.sections.ticketName;
        finalDesign = {
          ...finalDesign,
          sections: {
            ...finalDesign.sections,
            ticketName: {
              ...oldPill,
              type: "text",
              label: "Ticket Class",
              color: oldPill.textColor || oldPill.color || "#000000",
              fontFamily: oldPill.fontFamily || "Inter",
            },
          },
        };
      }

      // Migration: Ensure 'venue' exists and is correctly configured for regular tickets
      if (type !== "group" && tag !== "certificate" && tag !== "forCertificatePrint" && finalDesign?.sections) {
        const isBadgeTemplate = tag === "badge";
        if (!finalDesign.sections.venue) {
          finalDesign = {
            ...finalDesign,
            sections: {
              ...finalDesign.sections,
              venue: {
                enabled: isBadgeTemplate ? false : true,
                type: "text",
                label: "Venue",
                keyPath: "venue",
                keyPathFallbacks: ["venue", "location"],
                fontFamily: isBadgeTemplate ? "Roboto" : "Inter",
                fontWeight: isBadgeTemplate ? 700 : 400,
                fontSize: isBadgeTemplate ? 10 : 13,
                color: isBadgeTemplate ? "#000000" : "#ffffff",
                align: "center",
                x: isBadgeTemplate ? 10 : 11,
                y: isBadgeTemplate ? 200 : 47,
                width: isBadgeTemplate ? 222 : 299,
                spacing: isBadgeTemplate ? 4 : 15,
                height: isBadgeTemplate ? 18 : 44,
              },
            },
          };
        } else {
          const { text, ...rest } = finalDesign.sections.venue;
          finalDesign = {
            ...finalDesign,
            sections: {
              ...finalDesign.sections,
              venue: {
                ...rest,
                keyPathFallbacks: ["venue", "location"],
              },
            },
          };
        }

        if (!finalDesign.sectionsOrder.includes("venue")) {
          // Insert venue before bookedBy or at the end
          const bookedIdx = finalDesign.sectionsOrder.indexOf("bookedBy");
          const newOrder = [...finalDesign.sectionsOrder];
          if (bookedIdx !== -1) {
            newOrder.splice(bookedIdx, 0, "venue");
          } else {
            newOrder.push("venue");
          }
          finalDesign.sectionsOrder = newOrder;
        }
      }

      // Migration: Ensure 'companyName' exists and non-overlapping layout for badge templates
      if (tag === "badge" && finalDesign?.sections) {
        if (!finalDesign.sections.companyName) {
          finalDesign = {
            ...finalDesign,
            sections: {
              ...finalDesign.sections,
              companyName: {
                enabled: true,
                type: "text",
                label: "Company Name",
                keyPath: "company_name",
                keyPathFallbacks: ["Company Name", "company", "organization"],
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: 11,
                color: "#000000",
                align: "center",
                x: 10,
                y: 48,
                width: 222,
                spacing: 4,
              },
            },
          };
        }

        // Fix legacy overlapping default coordinates (y: 34 collided with multi-line attendee names)
        if (finalDesign.sections.companyName && Number(finalDesign.sections.companyName.y) <= 36) {
          const updatedAttendee = finalDesign.sections.attendeeName
            ? {
                ...finalDesign.sections.attendeeName,
                y: Math.min(Number(finalDesign.sections.attendeeName.y || 10), 10),
                fontSize: Math.min(Number(finalDesign.sections.attendeeName.fontSize || 15), 15),
              }
            : finalDesign.sections.attendeeName;

          const updatedQr = finalDesign.sections.qr && Number(finalDesign.sections.qr.y) <= 60
            ? {
                ...finalDesign.sections.qr,
                y: 68,
                width: 104,
                height: 104,
                x: 69,
              }
            : finalDesign.sections.qr;

          finalDesign = {
            ...finalDesign,
            sections: {
              ...finalDesign.sections,
              ...(updatedAttendee ? { attendeeName: updatedAttendee } : {}),
              companyName: {
                ...finalDesign.sections.companyName,
                y: 48,
              },
              ...(updatedQr ? { qr: updatedQr } : {}),
            },
          };
        }

        if (Array.isArray(finalDesign.sectionsOrder) && !finalDesign.sectionsOrder.includes("companyName")) {
          const attendeeIdx = finalDesign.sectionsOrder.indexOf("attendeeName");
          const newOrder = [...finalDesign.sectionsOrder];
          if (attendeeIdx !== -1) {
            newOrder.splice(attendeeIdx + 1, 0, "companyName");
          } else {
            newOrder.unshift("companyName");
          }
          finalDesign.sectionsOrder = newOrder;
        }
      }

      setInitialDesign(cloneDesign(finalDesign));
      setDesignState(finalDesign);
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneDesign, eventId, ticketId, storageKey, tag, type]);

  const updateDesign = (updaterOrPatch) => {
    setDesignState((prev) => {
      if (typeof updaterOrPatch === "function") {
        return updaterOrPatch(prev);
      }
      if (!prev) return updaterOrPatch;

      const next = { ...prev, ...updaterOrPatch };

      // NO autosave here – save only on button click
      return next;
    });
  };

  const replaceDesign = useCallback((nextDesign) => {
    setDesignState(nextDesign);
  }, []);

  const sections = useMemo(() => {
    if (!design) return [];
    return (design.sectionsOrder || [])
      .map((id) => ({ id, def: design.sections[id] }))
      .filter((s) => s.def);
  }, [design]);

  const markSavedDesign = useCallback(
    (nextDesign, nextPreGeneratePrintPdf = preGeneratePrintPdf) => {
      setInitialDesign(cloneDesign(nextDesign));
      setInitialPreGeneratePrintPdf(Boolean(nextPreGeneratePrintPdf));
    },
    [cloneDesign, preGeneratePrintPdf],
  );

  return {
    design,
    setDesign: updateDesign,
    replaceDesign,
    sections,
    loading,
    initialDesign,
    markSavedDesign,
    preGeneratePrintPdf,
    setPreGeneratePrintPdf,
    initialPreGeneratePrintPdf,
    hasCertificate,
    setHasCertificate,
  };
}
